// Libraries
import { useCallback, useEffect, useRef, useState } from 'react'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, Controller, useWatch } from 'react-hook-form'
import SimpleBar from 'simplebar-react'
import { REPEAT_EVERY_OPTIONS, repeatEveryOptionFromLevel } from '../repeatEvery'
// UI Icons
import {
  XClose,
  User01,
  Mail01,
  Building07,
  Briefcase02,
  Calendar,
  RefreshCcw05,
  Edit01,
  ChevronDown,
  Compass,
  CreditCard01,
  MarkerPin01,
} from '@untitled-ui/icons-react'
// Shared Components
import {
  MyButton,
  MyTextField,
  MyAutocomplete,
  MyAsyncDropdown,
  MyDoubleCard,
  MyHorizontalTabV2,
  MyDropzone,
  WhatsApp,
  MyCalendar,
  myToaster,
} from '@interstellar-component'
import { format } from 'date-fns'
import PhoneInput from '@src/components/PhoneInput'
import ConfirmEnquiryPaymentModal from './ConfirmEnquiryPaymentModal'
import { toNationalPhone, toE164Phone } from '@services/phone'
import { searchClikCities } from '@services/indonesiaAddress'
import { WorkforceService } from '@pages/Workforce/service'
import {
  CompanyPickerOption,
  isCompanySelectable,
  toCompanyOption,
} from '@pages/Company/components/MyMemberStatusChip'
import AuthImage from '@src/components/AuthImage'
import { API_BASE_URL } from '@src/runtimeConfig'
// Context & Service
import { useReportEnquiry } from '../Context'
import Service from '../service'

// The picker may hand back the raw stored path ("/uploads/<id>", without the
// service prefix). AuthImage only prepends the API origin, so resolve that form
// against the API base (same as SentToClikSlider.resolvePhotoUrl).
const resolveKtpPreviewSrc = (url) =>
  url?.startsWith('/uploads/') ? `${(API_BASE_URL || '').replace(/\/$/, '')}${url}` : url

// ── validation schema ──────────────────────────────────────────────────────────
// Every starred field is enforced for both targets. For an Employee the
// identity/employment fields are auto-filled from the picked workforce and stay
// read-only until "Edit employee details" is toggled on — making them required
// here means an emptied field (e.g. a cleared Email) blocks Submit instead of
// silently wiping the workforce on save.
const schema = yup.object({
  category: yup
    .string()
    .oneOf(['Employee', 'Candidate'])
    .required('Category is required'),

  // Full name — required for both. A picked employee is an object
  // ({ label, value, ... }); guard against an emptied label in edit mode.
  fullName: yup.lazy((value) =>
    typeof value === 'object' && value !== null
      ? yup
          .mixed()
          .test(
            'employee-name-required',
            'Full name is required',
            (v) => Boolean((v?.label ?? '').trim())
          )
      : yup.string().when('category', {
          is: 'Candidate',
          then: (s) =>
            s
              .trim()
              .required('Full name is required')
              .min(2, 'Full name must be 2-100 characters')
              .max(100, 'Full name must be 2-100 characters'),
          otherwise: (s) => s.required('Full name is required'),
        })
  ),

  // Entity, Level & Email — required for both Candidate and Employee.
  entity: yup
    .object({ label: yup.string(), value: yup.string() })
    .nullable()
    .required('Entity is required'),
  level: yup
    .object({ label: yup.string(), value: yup.string() })
    .nullable()
    .required('Level is required'),
  email: yup
    .string()
    .trim()
    .nullable()
    .required('Email is required')
    .email('Invalid email'),

  // Employee-specific fields
  consentExpiry: yup
    .date()
    .nullable()
    .when('category', {
      is: 'Employee',
      then: (s) =>
        s
          .required('Consent expiry is required')
          .test(
            'not-past',
            'Consent expiry must not be in the past',
            (value) => {
              if (!value) return true
              const today = new Date()
              today.setHours(0, 0, 0, 0)
              return new Date(value) >= today
            }
          ),
      otherwise: (s) => s.nullable(),
    }),
  repeatEvery: yup
    .object({ label: yup.string(), value: yup.string() })
    .nullable()
    .when('category', {
      is: 'Employee',
      then: (s) => s.required('Repeat every is required'),
      otherwise: (s) => s.nullable(),
    }),

  // Position & WhatsApp — shown only for Employee, so required there only.
  position: yup
    .object({ label: yup.string(), value: yup.string() })
    .nullable()
    .when('category', {
      is: 'Employee',
      then: (s) => s.required('Position is required'),
      otherwise: (s) => s.nullable(),
    }),
  whatsapp: yup
    .string()
    .nullable()
    .when('category', {
      is: 'Employee',
      then: (s) => s.trim().required('WhatsApp number is required'),
      otherwise: (s) => s.nullable(),
    }),

  // ── HR e-KTP fields (Candidate + holding ektp_upload_process='hr') ──────
  // `ektpByHr` is a hidden flag mirrored from GET /consent-config for the
  // selected entity. The KTP block is only rendered + required when both the
  // target is Candidate and the holding makes HR supply the KTP.
  ektpByHr: yup.boolean(),
  // Repeat enquiry: the picked employee's previous KYC record, whose KTP photo
  // the BE copies (POST /:id/ktp source_kyc_record_id) when HR doesn't upload a
  // new one. Set only by handleEmployeeChange; cleared on a new upload.
  ktpPhotoSourceKycId: yup.string().nullable(),
  ktpPhoto: yup
    .array()
    .when(['ektpByHr', 'ktpPhotoSourceKycId'], {
      is: (byHr, sourceKycId) => byHr === true && !sourceKycId,
      then: (s) => s.min(1, 'KTP photo is required'),
      otherwise: (s) => s.nullable(),
    }),

  // ── HR supporting documents (per-enquiry, when the holding mode is 'hr'). ──
  // jobFormByHr also requires a Candidate target (employees have no job
  // application form). Both mirror GET /consent-config for the picked entity.
  jobFormByHr: yup.boolean(),
  jobFormFile: yup
    .array()
    .when('jobFormByHr', {
      is: true,
      then: (s) => s.min(1, 'Job application form file is required'),
      otherwise: (s) => s.nullable(),
    }),
  tncByHr: yup.boolean(),
  tncFile: yup
    .array()
    .when('tncByHr', {
      is: true,
      then: (s) => s.min(1, 'Terms & condition file is required'),
      otherwise: (s) => s.nullable(),
    }),
  ktpNumber: yup
    .string()
    .nullable()
    .when('ektpByHr', {
      is: true,
      then: (s) =>
        s
          .trim()
          .required('NIK is required')
          .matches(/^\d{16}$/, 'NIK must be exactly 16 digits'),
      otherwise: (s) => s.nullable(),
    }),
  ktpName: yup
    .string()
    .nullable()
    .when('ektpByHr', {
      is: true,
      then: (s) =>
        s
          .trim()
          .required('Name on KTP is required')
          .min(2, 'Name must be 2-100 characters')
          .max(100, 'Name must be 2-100 characters'),
      otherwise: (s) => s.nullable(),
    }),
  ktpDob: yup
    .date()
    .nullable()
    .when('ektpByHr', {
      is: true,
      then: (s) =>
        s
          .required('Date of birth is required')
          .max(new Date(), 'Date of birth cannot be in the future'),
      otherwise: (s) => s.nullable(),
    }),
  ktpGender: yup
    .object({ label: yup.string(), value: yup.string() })
    .nullable()
    .when('ektpByHr', {
      is: true,
      then: (s) => s.required('Gender is required'),
      otherwise: (s) => s.nullable(),
    }),
  ktpAddress: yup
    .string()
    .nullable()
    .when('ektpByHr', {
      is: true,
      then: (s) =>
        s
          .trim()
          .required('Address is required')
          .min(2, 'Address must be 2-500 characters')
          .max(500, 'Address must be 2-500 characters'),
      otherwise: (s) => s.nullable(),
    }),
  // ── Region (feeds the CLIK enquiry address). City carries the CLIK
  // RegionDomain code (→ ktp_city_code → address.city); district/subdistrict are
  // names (→ address.district / subDistrict); postal code → address.postalCode.
  // Required so an HR-supplied KTP doesn't fall back to CLIK's "00000" default.
  ktpCity: yup
    .object({ id: yup.string(), name: yup.string(), code: yup.string() })
    .nullable()
    .when('ektpByHr', {
      is: true,
      then: (s) => s.required('City is required'),
      otherwise: (s) => s.nullable(),
    }),
  ktpDistrict: yup
    .string()
    .nullable()
    .when('ektpByHr', {
      is: true,
      then: (s) =>
        s
          .trim()
          .required('District is required')
          .max(100, 'District must be at most 100 characters'),
      otherwise: (s) => s.nullable(),
    }),
  ktpSubdistrict: yup
    .string()
    .nullable()
    .when('ektpByHr', {
      is: true,
      then: (s) =>
        s
          .trim()
          .required('Subdistrict is required')
          .max(100, 'Subdistrict must be at most 100 characters'),
      otherwise: (s) => s.nullable(),
    }),
  // Optional (CLIK accepts an empty postal code); checked only when filled.
  ktpPostalCode: yup
    .string()
    .nullable()
    .when('ektpByHr', {
      is: true,
      then: (s) =>
        s
          .trim()
          .matches(/^\d{4,10}$/, { message: 'Postal code must be 4-10 digits', excludeEmptyString: true }),
      otherwise: (s) => s.nullable(),
    }),
  // Optional — CLIK accepts empty values for these.
  ktpWhatsapp: yup.string().nullable(),
})

// ── static options ────────────────────────────────────────────────────────────
// Stored as male/female to match normalizeGender() in clik-dispatcher and the
// verification display in the backoffice.
const KTP_GENDER_OPTIONS = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
]

// Workforce.gender is free text (onboarding imports vary: 'L'/'P',
// 'male'/'female', 'Laki-laki'/'Perempuan'). Map to a KTP_GENDER_OPTIONS entry
// for prefill; unknown → null so HR picks it manually.
function normalizeGenderToOption(raw) {
  if (!raw) return null
  const k = String(raw).trim().toLowerCase()
  if (['male', 'laki-laki', 'laki', 'l', 'm', 'pria'].includes(k)) return KTP_GENDER_OPTIONS[0]
  if (['female', 'perempuan', 'p', 'f', 'wanita'].includes(k)) return KTP_GENDER_OPTIONS[1]
  return null
}

// Parse a DATEONLY 'YYYY-MM-DD' into a LOCAL Date (avoids the UTC day-shift of
// new Date('YYYY-MM-DD')). Returns null on anything unparseable.
function parseDobToDate(value) {
  if (!value) return null
  const m = String(value).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

// MyAsyncDropdown contract: receives { search }, returns Promise<{ loading, data }>.
// Repeat Every options are static on the FE, so we just resolve the local list
// (optionally filtered by the typed keyword).
const fetchRepeatEveryOptions = async ({ search = '' } = {}) => {
  const keyword = search.trim().toLowerCase()
  return {
    loading: false,
    data: keyword
      ? REPEAT_EVERY_OPTIONS.filter((o) =>
          o.label.toLowerCase().includes(keyword)
        )
      : REPEAT_EVERY_OPTIONS,
  }
}

// ── helpers ────────────────────────────────────────────────────────────────────
function FieldLabel({ children, required }) {
  return (
    <p className="text-sm font-medium text-gray-700 mb-1">
      {children}
      {required && <span className="ml-0.5 text-brand/900">*</span>}
    </p>
  )
}

function SelectField({
  label,
  required,
  name,
  control,
  options,
  placeholder,
  errors,
  startAdornment,
  ...props
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <FieldLabel required={required}>{label}</FieldLabel>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <MyAutocomplete
            name={name}
            options={options}
            placeholder={placeholder}
            error={errors}
            startAdornment={startAdornment}
            value={field.value}
            onChange={(_e, val) => field.onChange(val)}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            getOptionLabel={(e) => e?.label || ''}
            {...props}
          />
        )}
      />
    </div>
  )
}

// Human labels for the required-field summary shown on an invalid submit. Keys
// are react-hook-form field names; anything unmapped falls back to its key.
const FIELD_LABELS = {
  category: 'Target',
  fullName: 'Full name',
  entity: 'Entity',
  level: 'Level',
  position: 'Position',
  whatsapp: 'WhatsApp',
  email: 'Email',
  consentExpiry: 'Consent Expiry',
  repeatEvery: 'Repeat Every',
  ktpPhoto: 'KTP photo',
  ktpNumber: 'NIK',
  ktpName: 'Name on KTP',
  ktpDob: 'Date of birth',
  ktpGender: 'Gender',
  ktpAddress: 'Address',
  ktpCity: 'City',
  ktpDistrict: 'District',
  ktpSubdistrict: 'Subdistrict',
  ktpPostalCode: 'Postal code',
  ktpWhatsapp: 'WhatsApp',
}

// ── main component ─────────────────────────────────────────────────────────────
function NewEnquirySlider() {
  const {
    pushSlider,
    popSlider,
    handleCurrentSlider,
    sliderStack,
    getList,
  } = useReportEnquiry()

  // Anything stacked on top of new-enquiry (e.g. employee-list) locks the
  // form fields + Submit. Cancel stays active so the user can always bail.
  const isStacked = sliderStack.length > 1

  // Bumped to remount the KTP dropzone (see the MyDropzone key below).
  const [ktpDropzoneSeq, setKtpDropzoneSeq] = useState(0)
  const [entityOptions, setEntityOptions] = useState([])
  const [levelOptions, setLevelOptions] = useState([])
  const [positionOptions, setPositionOptions] = useState([])

  // "Edit employee details" mode. While on, the employment fields unlock and
  // the picked employee is updated (PUT /v1/workforce/:id) on Submit before the
  // enquiry is created. `originalEmployee` is the snapshot taken at selection,
  // used to send only the fields the user actually changed.
  const [editing, setEditing] = useState(false)
  const [originalEmployee, setOriginalEmployee] = useState(null)
  // Confirm-payment modal (per-enquiry payment). Opened only when quota does not
  // cover the enquiry; carries the built payload + attachments so its confirm can
  // finalise the create with the chosen method.
  const [payOpen, setPayOpen] = useState(false)
  const [payPreview, setPayPreview] = useState(null)
  const [pendingCreate, setPendingCreate] = useState(null)

  // Required-field summary + scroll anchor for an invalid submit. Many fields
  // (KTP block) sit far below the fold, and react-hook-form's shouldFocusError
  // can't focus the custom inputs — so a plain Submit looked like nothing
  // happened. onInvalid (below) fills this and scrolls to the first error.
  const [errorSummary, setErrorSummary] = useState([])
  const formScrollRef = useRef(null)

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      category: 'Candidate',
      entity: null,
      fullName: '',
      level: null,
      position: null,
      whatsapp: '',
      email: '',
      consentExpiry: null,
      repeatEvery: null,
      // HR e-KTP block (Candidate + holding ektp='hr')
      ektpByHr: false,
      ktpPhoto: [],
      ktpPhotoSourceKycId: null,
      ktpPhotoSourceUrl: null,
      ktpNumber: '',
      ktpName: '',
      ktpDob: null,
      ktpGender: null,
      ktpAddress: '',
      ktpCity: null,
      ktpDistrict: '',
      ktpSubdistrict: '',
      ktpPostalCode: '',
      ktpWhatsapp: '',
      // HR supporting documents (per-enquiry, holding mode 'hr')
      jobFormByHr: false,
      jobFormFile: [],
      tncByHr: false,
      tncFile: [],
    },
  })

  const category = useWatch({ control, name: 'category' })

  // In Employee mode the employment fields (entity/level/position/whatsapp/
  // email/edit) are auto-filled from the picked workforce, so they stay
  // read-only even when the list slider is closed — only Full name (re-pick),
  // Target toggle, and the Request option block remain editable.
  const isEmployeeReadonly = category === 'Employee'

  const selectedEmployee = useWatch({ control, name: 'fullName' })
  const hasSelectedEmployee = Boolean(selectedEmployee?.value)

  // The HR e-KTP block depends on the selected entity's holding settings
  // (ektp_upload_process). `ektpByHr` is a hidden form flag the schema + render
  // key off; it's only ever true for a Candidate against an 'hr' holding.
  const entityValue = useWatch({ control, name: 'entity' })
  const ektpByHr = useWatch({ control, name: 'ektpByHr' })
  const jobFormByHr = useWatch({ control, name: 'jobFormByHr' })
  const tncByHr = useWatch({ control, name: 'tncByHr' })
  const ktpCity = useWatch({ control, name: 'ktpCity' })
  const ktpPhotoSourceKycId = useWatch({ control, name: 'ktpPhotoSourceKycId' })
  const ktpPhotoSourceUrl = useWatch({ control, name: 'ktpPhotoSourceUrl' })
  const [ektpConfigLoading, setEktpConfigLoading] = useState(false)

  // Switching category leaves edit mode (the employee block hides/changes).
  useEffect(() => {
    setEditing(false)
  }, [category])

  // Resolve the consent-flow config for the picked entity. Both Candidate and
  // Employee enquiries run the same consent flow, so HR supplies the e-KTP for
  // either target when the holding is ektp='hr'. For Employee the entity is
  // auto-filled from the picked workforce.
  useEffect(() => {
    if (!entityValue?.value) {
      setValue('ektpByHr', false)
      setValue('jobFormByHr', false)
      setValue('tncByHr', false)
      return
    }
    let active = true
    setEktpConfigLoading(true)
    Service.getConsentConfig({ company_id: entityValue.value })
      .then((res) => {
        if (!active) return
        const cfg = res?.data ?? {}
        const isHr = (cfg.ektp_upload_process ?? 'hr') === 'hr'
        setValue('ektpByHr', isHr)
        // Job application form file: HR mode AND Candidate target only.
        setValue(
          'jobFormByHr',
          cfg.job_application_form_mode === 'hr' && category === 'Candidate',
        )
        // T&C file: HR mode, for either Candidate or Employee.
        setValue('tncByHr', cfg.terms_condition_mode === 'hr')
      })
      .catch(() => {
        if (active) {
          setValue('ektpByHr', false)
          setValue('jobFormByHr', false)
          setValue('tncByHr', false)
        }
      })
      .finally(() => {
        if (active) setEktpConfigLoading(false)
      })
    return () => {
      active = false
    }
  }, [category, entityValue?.value, setValue])

  // ── Fetch dropdown options ────────────────────────────────────────────
  const fetchOptions = useCallback(() => {
    Service.getCompanyOptions()
      .then((res) => {
        // Keeps membership fields so non-active companies render disabled + chip.
        setEntityOptions((res.data || []).map(toCompanyOption))
      })
      .catch(() => { })

    Service.getLevelOptions()
      .then((res) => {
        // Keep the level's consent config so selecting an employee can default
        // Consent Expiry + Repeat Every from its level (Everest #86eybg411).
        setLevelOptions(
          (res.data || []).map((l) => ({
            label: l.name,
            value: l.id,
            consent_expiry: l.consent_expiry,
            repeat_every: l.repeat_every,
          }))
        )
      })
      .catch(() => { })

    Service.getPositionOptions()
      .then((res) => {
        setPositionOptions(
          (res.data || []).map((p) => ({ label: p.name, value: p.id }))
        )
      })
      .catch(() => { })
  }, [])

  useEffect(() => {
    fetchOptions()
  }, [fetchOptions])

  // ── Submit ────────────────────────────────────────────────────────────
  // Invalid submit: list the offending fields and jump to the first one. Needed
  // because the KTP block is long and mostly below the fold — without this the
  // Submit button did nothing visible when a hidden field failed validation
  // (react-hook-form's shouldFocusError can't focus the custom inputs).
  const onInvalid = (formErrors) => {
    setErrorSummary(Object.keys(formErrors).map((k) => FIELD_LABELS[k] || k))
    // The summary banner uses warning (amber) colours, so this red text-error
    // query skips it and lands on the first real field error.
    requestAnimationFrame(() => {
      const el = formScrollRef.current?.querySelector('[class*="text-error"]')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }

  const onSubmit = handleSubmit(async (data) => {
    setErrorSummary([])
    const isEmployee = data.category === 'Employee'

    // Persist edited employee details first (only the changed fields). If this
    // PUT fails we abort and DON'T create the enquiry, so the user can retry.
    if (isEmployee && editing) {
      const workforceId = data.fullName?.value
      const orig = originalEmployee || {}
      const changes = {}
      const newName = (data.fullName?.label ?? '').trim()
      if (newName && newName !== (orig.label ?? '').trim()) changes.full_name = newName
      if (data.entity?.value && data.entity.value !== orig.entity?.value)
        changes.company_id = data.entity.value
      if (data.level?.value && data.level.value !== orig.level?.value)
        changes.employment_level_id = data.level.value
      if (data.position?.value && data.position.value !== orig.position?.value)
        changes.position_id = data.position.value
      const newPhone = (data.whatsapp ?? '').trim()
      // Compare in national form; persist in the canonical +62 (E.164) format.
      if (newPhone && newPhone !== toNationalPhone(orig.whatsapp)) changes.phone = toE164Phone(newPhone)
      const newEmail = (data.email ?? '').trim()
      if (newEmail !== (orig.email ?? '').trim()) changes.email = newEmail

      if (workforceId && Object.keys(changes).length > 0) {
        try {
          await WorkforceService.updateWorkforce(workforceId, changes)
        } catch (err) {
          myToaster(err)
          return
        }
      }
    }

    const payload = isEmployee
      ? {
          category: 'employee',
          workforce_id: data.fullName?.value,
          consent_expiry: data.consentExpiry
            ? format(data.consentExpiry, 'yyyy-MM-dd')
            : undefined,
          repeat_every: data.repeatEvery?.value,
        }
      : {
          category: 'candidate',
          company_id: data.entity?.value,
          full_name:
            typeof data.fullName === 'string'
              ? data.fullName
              : data.fullName?.label,
          employment_level_id: data.level?.value,
          email: data.email,
        }

    // Attachments to run after create, captured so both the direct (quota-
    // covered) path and the pay-modal path finalise the same way.
    const attachments = {
      jobFormByHr: data.jobFormByHr, jobFormFile: data.jobFormFile,
      tncByHr: data.tncByHr, tncFile: data.tncFile,
      ektpByHr: data.ektpByHr, ktpPhoto: data.ktpPhoto,
      ktpPhotoSourceKycId: data.ktpPhotoSourceKycId, ktpNumber: data.ktpNumber,
      ktpName: data.ktpName, ktpDob: data.ktpDob, ktpGender: data.ktpGender,
      ktpAddress: data.ktpAddress, ktpCity: data.ktpCity, ktpDistrict: data.ktpDistrict,
      ktpSubdistrict: data.ktpSubdistrict, ktpPostalCode: data.ktpPostalCode,
      ktpWhatsapp: data.ktpWhatsapp,
    }

    // Quota comes first: if the company still has a free/bonus unit this enquiry
    // is free and no payment step is shown. Only when quota is gone do we open
    // the Confirm-payment modal (Everest saldo, no PPN, vs gateway/VA + PPN).
    const companyId = data.entity?.value
    let prev = null
    try {
      const r = await Service.previewPayment({ company_id: companyId })
      prev = r?.data ?? r
    } catch {
      // Preview failed — fall through to a plain create; the server still funds
      // it quota-first and parks it pending if short, exactly as before.
    }
    if (!prev || prev.covered_by_quota || !prev.price_determinable) {
      // Quota-covered, preview-failed, or unpriceable (e.g. the company has no
      // active subscription) → create directly, no payment step. The create can
      // still be REJECTED by the server — most notably 402 "Company has no active
      // subscription. Subscribe to a plan to use EBC service." — and this path
      // had no catch, so the error was swallowed: nothing on screen, only a 402
      // in the network tab. Surface it so the operator sees why it didn't go
      // through (Everest: "tidak ada notifikasi kalau melakukan enquiry tetapi
      // belum berlangganan").
      try {
        await finalizeCreate(payload, attachments, { closeOnDone: true })
      } catch (err) {
        myToaster({
          status: err?.response?.status ?? 400,
          title: 'Enquiry could not be created',
          message:
            err?.response?.data?.message ||
            err?.message ||
            'Failed to create the enquiry. Try again.',
        })
      }
      return
    }
    setPendingCreate({ payload, attachments, companyId })
    setPayPreview(prev)
    setPayOpen(true)
  }, onInvalid)

  // Create the enquiry then run its attachments (doc/KTP). Returns the create
  // response so the caller can read gateway_payment. Does NOT close on its own
  // unless asked — the pay modal keeps itself open to show the VA.
  const finalizeCreate = async (payload, attachments, { closeOnDone } = {}) => {
    const res = await Service.create(payload)
    const enquiryId = res?.data?.id

    const uploadDoc = async (file, documentType) => {
      const f = Array.isArray(file) ? file[0] : file
      if (!enquiryId || !f) return
      const fd = new FormData()
      fd.append('file', f)
      fd.append('document_type', documentType)
      await Service.uploadDocument(enquiryId, fd)
    }
    if (attachments.jobFormByHr) await uploadDoc(attachments.jobFormFile, 'job_application_form')
    if (attachments.tncByHr) await uploadDoc(attachments.tncFile, 'terms_condition')

    if (attachments.ektpByHr) {
      const ktpFile = Array.isArray(attachments.ktpPhoto) ? attachments.ktpPhoto[0] : null
      const sourceKycId = attachments.ktpPhotoSourceKycId
      if (!enquiryId) throw new Error('Enquiry created but no id returned — cannot attach KTP.')
      if (!ktpFile && !sourceKycId) throw new Error('KTP photo is required.')
      const fd = new FormData()
      // A newly uploaded file wins; otherwise the BE copies the photo from the
      // employee's previous KYC record (repeat enquiry).
      if (ktpFile) fd.append('file', ktpFile)
      else fd.append('source_kyc_record_id', sourceKycId)
      fd.append('ktp_number', attachments.ktpNumber)
      fd.append('ktp_name', attachments.ktpName)
      fd.append('ktp_date_of_birth', format(attachments.ktpDob, 'yyyy-MM-dd'))
      fd.append('ktp_gender', attachments.ktpGender?.value)
      fd.append('ktp_address', attachments.ktpAddress)
      fd.append('ktp_city', attachments.ktpCity?.name ?? '')
      fd.append('ktp_city_code', attachments.ktpCity?.code ?? '')
      fd.append('ktp_district', attachments.ktpDistrict ?? '')
      fd.append('ktp_village', attachments.ktpSubdistrict ?? '')
      fd.append('ktp_postal_code', attachments.ktpPostalCode ?? '')
      if (attachments.ktpWhatsapp?.trim()) fd.append('whatsapp_number', attachments.ktpWhatsapp.trim())
      await Service.uploadKtp(enquiryId, fd)
    }

    if (closeOnDone) {
      myToaster(res)
      getList()
      handleCurrentSlider(null)
    }
    return res
  }

  const handleClose = () => handleCurrentSlider(null)

  const handleEmployeeChange = (e, val) => {
    if (val) {
      // shouldValidate clears the "required" errors that yup raised earlier
      // when the field was empty — RHF doesn't auto-revalidate on setValue.
      const opts = { shouldValidate: true, shouldDirty: true }
      setValue('entity', val.entity, opts)
      setValue('level', val.level, opts)
      setValue('position', val.position, opts)

      // Default Consent Expiry + Repeat Every from the selected employee's
      // employment level (Everest #86eybg411). levelOptions carries the level's
      // consent config; look it up by id. HR can still override both fields.
      const levelCfg = levelOptions.find((o) => o.value === (val.level?.value ?? val.level))
      if (levelCfg) {
        const levelExpiry = parseDobToDate(levelCfg.consent_expiry)
        if (levelExpiry) setValue('consentExpiry', levelExpiry, opts)
        const levelRepeat = repeatEveryOptionFromLevel(levelCfg.repeat_every)
        if (levelRepeat) setValue('repeatEvery', levelRepeat, opts)
      }
      setValue('whatsapp', toNationalPhone(val.whatsapp), opts)
      setValue('email', val.email, opts)

      // Prefill the HR e-KTP identity from the employee's on-file data. Only
      // surfaces if the holding is ektp='hr' (resolved via consent-config on
      // the entity change above); HR can still edit. The KTP photo is reused
      // from the last KYC record when there is one (see below).
      setValue('ktpNumber', val.id_number || '')
      setValue('ktpName', val.ktp_name || val.full_name || val.label || '')
      setValue('ktpDob', parseDobToDate(val.date_of_birth))
      setValue('ktpGender', normalizeGenderToOption(val.gender))
      setValue('ktpAddress', val.address || '')
      setValue('ktpWhatsapp', toNationalPhone(val.whatsapp) || '')
      // Region from the last captured e-KTP. The City dropdown matches options
      // by CLIK code (isOptionEqualToValue), so the prefill needs the code.
      setValue(
        'ktpCity',
        val.city?.code ? { id: val.city.code, name: val.city.name, code: val.city.code } : null,
      )
      setValue('ktpDistrict', val.district || '')
      setValue('ktpSubdistrict', val.subdistrict || '')
      setValue('ktpPostalCode', val.postal_code || '')
      // KTP photo: reuse the previous one by reference (the BE copies it) —
      // never carry a file picked for a different employee over.
      const hasPrevKtpPhoto = Boolean(val.ktp_photo_url && val.kyc_record_id)
      setValue('ktpPhoto', [])
      setKtpDropzoneSeq((n) => n + 1)
      setValue('ktpPhotoSourceKycId', hasPrevKtpPhoto ? val.kyc_record_id : null)
      setValue('ktpPhotoSourceUrl', hasPrevKtpPhoto ? val.ktp_photo_url : null)
    }
  }

  const openEmployeeList = () => {
    pushSlider({
      current: 'employee-list',
      props: {
        onSelect: (emp) => {
          setValue('fullName', emp, { shouldValidate: true, shouldDirty: true })
          handleEmployeeChange(null, emp)
          setOriginalEmployee(emp)
          setEditing(false)
        },
      },
    })
  }

  const closeEmployeeListIfOpen = () => {
    if (sliderStack.some((s) => s.current === 'employee-list')) {
      popSlider()
    }
  }

  return (
    <div className="flex h-screen w-[420px] flex-col bg-white shadow-xl">
      {/* Header */}
      <header className="relative flex items-start gap-x-4 px-6 py-6 border-b border-gray-100">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-[12px] top-[12px] flex h-10 w-10 items-center justify-center rounded-lg p-2 text-gray-400 hover:bg-gray-50 active:bg-gray-100"
        >
          <XClose size={24} stroke="currentColor" />
        </button>

        <div className="flex flex-1 flex-col gap-1 pt-1">
          <p className="text-lg font-semibold text-gray-900">New Request</p>
          <p className="text-sm text-gray-500 font-medium">
            Create a new enquiry request
          </p>
        </div>
      </header>

      {/* Scrollable body */}
      <form noValidate onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden">
        <section
          ref={formScrollRef}
          aria-disabled={isStacked}
          className={`flex-1 overflow-hidden ${
            isStacked ? 'pointer-events-none select-none' : ''
          }`}
        >
          <SimpleBar forceVisible="y" style={{ maxHeight: '100%' }}>
            <div className="flex flex-col gap-6 px-6 py-6 bg-gray-50/30">
              {/* Required-field summary on an invalid submit. Warning (amber)
                  colours on purpose: the onInvalid scroll-to-error query looks
                  for `text-error`, so this banner is skipped and the scroll
                  lands on the first real field. */}
              {errorSummary.length > 0 && (
                <div className="rounded-lg border border-warning/300 bg-warning/50 px-4 py-3">
                  <p className="text-sm font-semibold text-warning/700">
                    {errorSummary.length} required{' '}
                    {errorSummary.length === 1 ? 'field is' : 'fields are'} incomplete
                  </p>
                  <p className="mt-0.5 text-sm text-warning/700">
                    Complete: {errorSummary.join(', ')}.
                  </p>
                </div>
              )}
              {/* General Information card */}
              <MyDoubleCard heading="General Information" innerClassName="p-4">
                <div className="flex flex-col gap-5">
                  {/* Category toggle — stays interactive even when the employee-
                      list slider is stacked on top (which sets the section to
                      pointer-events-none). It's the back-out control: the user
                      must be able to switch to Candidate to close that list. */}
                  <div className={`flex flex-col gap-1.5 ${isStacked ? 'pointer-events-auto' : ''}`}>
                    <FieldLabel>Target</FieldLabel>
                    <Controller
                      name="category"
                      control={control}
                      render={({ field }) => (
                        <MyHorizontalTabV2
                          fitContent
                          value={field.value}
                          onChange={(val) => {
                            field.onChange(val)
                            setValue('fullName', '')
                            setValue('entity', null)
                            setValue('level', null)
                            setValue('position', null)
                            setValue('whatsapp', '')
                            setValue('email', '')
                            setValue('consentExpiry', null)
                            setValue('repeatEvery', null)
                            setValue('ektpByHr', false)
                            setValue('ktpPhoto', [])
                            setKtpDropzoneSeq((n) => n + 1)
                            setValue('ktpPhotoSourceKycId', null)
                            setValue('ktpPhotoSourceUrl', null)
                            setValue('ktpNumber', '')
                            setValue('ktpName', '')
                            setValue('ktpDob', null)
                            setValue('ktpGender', null)
                            setValue('ktpAddress', '')
                            setValue('ktpCity', null)
                            setValue('ktpDistrict', '')
                            setValue('ktpSubdistrict', '')
                            setValue('ktpPostalCode', '')
                            setValue('ktpWhatsapp', '')
                            clearErrors()
                            setErrorSummary([])
                            if (val === 'Employee') {
                              openEmployeeList()
                            } else {
                              closeEmployeeListIfOpen()
                            }
                          }}
                          tabs={[
                            { label: 'Candidate', value: 'Candidate' },
                            { label: 'Employee', value: 'Employee' },
                          ]}
                        />
                      )}
                    />
                    {errors.category && (
                      <p className="text-sm text-error/600 mt-0.5">{errors.category.message}</p>
                    )}
                  </div>

                  {/* Full name */}
                  <div className="flex flex-col gap-0.5">
                    <FieldLabel required>Full name</FieldLabel>
                    {category === 'Employee' && editing ? (
                      <Controller
                        name="fullName"
                        control={control}
                        render={({ field }) => (
                          <MyTextField
                            value={field.value?.label ?? ''}
                            onChangeForm={(e) =>
                              field.onChange({ ...field.value, label: e.target.value })
                            }
                            placeholder="e.g. Kania Elfira"
                            errors={errors?.fullName?.message}
                            startAdornment={<User01 className="size-4 text-gray-400" />}
                          />
                        )}
                      />
                    ) : category === 'Employee' ? (
                      <Controller
                        name="fullName"
                        control={control}
                        render={({ field: { value } }) => (
                          <div className="flex flex-col gap-1.5">
                            <button
                              type="button"
                              onClick={openEmployeeList}
                              className={`flex items-center gap-3 w-full rounded-lg border bg-gray-50 px-3.5 py-2.5 text-left transition-all ${errors.fullName
                                ? 'border-red-300 ring-1 ring-red-300'
                                : 'border-gray-300 hover:border-brand/400 hover:ring-1 hover:ring-brand/400'
                                }`}
                            >
                              {value?.avatar ? (
                                <img
                                  src={value.avatar}
                                  alt={value.label}
                                  className="size-5 rounded-full border border-gray-200 object-cover shrink-0"
                                />
                              ) : (
                                <User01 className="size-4 text-gray-400 shrink-0" />
                              )}
                              <div className="flex flex-1 flex-col overflow-hidden">
                                {value ? (
                                  <div className="flex items-baseline gap-2 truncate">
                                    <span className="text-sm font-medium text-gray-900">
                                      {value.label}
                                    </span>
                                    {value.supportingText && (
                                      <span className="text-xs text-gray-500 truncate">
                                        {value.supportingText}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-md-regular text-gray-light/500">
                                    Search employee
                                  </span>
                                )}
                              </div>
                              <XClose
                                className="size-5 text-gray-400 rotate-[-90deg]"
                                strokeWidth={2}
                              />
                            </button>
                            {errors.fullName && (
                              <p className="text-sm text-error/600">
                                {errors.fullName.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    ) : (
                      <MyTextField
                        name="fullName"
                        control={control}
                        placeholder="e.g. Kania Elfira"
                        errors={errors?.fullName?.message}
                        startAdornment={<User01 className="size-4 text-gray-400" />}
                      />
                    )}
                  </div>

                  {/* Entity */}
                  <SelectField
                    label="Entity"
                    required
                    name="entity"
                    control={control}
                    options={entityOptions}
                    placeholder="Select entity"
                    errors={errors?.entity?.message ?? errors?.entity?.value?.message}
                    startAdornment={<Building07 className="size-4 text-gray-400" />}
                    disabled={isEmployeeReadonly && !editing}
                    getOptionDisabled={(option) => !isCompanySelectable(option)}
                    renderOption={(option) => (
                      <CompanyPickerOption company={option} label={option?.label} />
                    )}
                  />

                  {/* Level */}
                  <SelectField
                    label="Level"
                    required
                    name="level"
                    control={control}
                    options={levelOptions}
                    placeholder="Select level"
                    errors={errors?.level?.message ?? errors?.level?.value?.message}
                    startAdornment={<Briefcase02 className="size-4 text-gray-400" />}
                    disabled={isEmployeeReadonly && !editing}
                  />

                  {/* Position (Employee only) */}
                  {category === 'Employee' && (
                    <SelectField
                      label="Position"
                      required
                      name="position"
                      control={control}
                      options={positionOptions}
                      placeholder="Select position"
                      errors={errors?.position?.message ?? errors?.position?.value?.message}
                      startAdornment={<Compass className="size-4 text-gray-400" />}
                      disabled={!editing}
                    />
                  )}

                  {/* WhatsApp (Employee only) */}
                  {category === 'Employee' && (
                    <div className="flex flex-col gap-0.5">
                      <FieldLabel required>WhatsApp</FieldLabel>
                      <PhoneInput
                        name="whatsapp"
                        control={control}
                        errors={errors?.whatsapp?.message}
                        startIcon={<WhatsApp className="size-4 text-green-500" />}
                        disabled={!editing}
                      />
                    </div>
                  )}

                  {/* Email */}
                  <div className="flex flex-col gap-0.5">
                    <FieldLabel required>Email</FieldLabel>
                    <MyTextField
                      name="email"
                      control={control}
                      placeholder="e.g. kania.elfira@starling.com"
                      errors={errors?.email?.message}
                      startAdornment={<Mail01 className="size-4 text-gray-400" />}
                      disabled={isEmployeeReadonly && !editing}
                    />
                    <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                      {category === 'Employee'
                        ? 'This email will be used to send the Credit Report form link to the employee.'
                        : 'Used as an alternative method for sending the form link.'}
                    </p>
                  </div>

                  {/* Edit action for employee */}
                  {category === 'Employee' && (
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => setEditing(true)}
                        disabled={editing || !hasSelectedEmployee}
                        className="flex items-center gap-2 text-sm font-semibold text-brand/700 hover:text-brand/800 w-max disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        <Edit01 className="size-4" />
                        Edit employee details
                      </button>
                    </div>
                  )}
                </div>
              </MyDoubleCard>

              {/* HR e-KTP card (holding ektp_upload_process='hr', Candidate or
                  Employee). For Employee the identity is prefilled from the
                  workforce record (handleEmployeeChange); HR can edit it. */}
              {ektpByHr && (
                <MyDoubleCard heading="Identity (e-KTP)" innerClassName="p-4">
                  <div className="flex flex-col gap-5">
                    <p className="text-xs text-gray-500 leading-relaxed">
                      This entity has HR supply the e-KTP. The data below feeds
                      the credit bureau enquiry and the candidate's face match.
                      {category === 'Employee' &&
                        ' Identity is prefilled from the employee record — edit if needed.'}
                    </p>

                    {/* KTP photo */}
                    <div className="flex flex-col gap-1.5">
                      <FieldLabel required>KTP photo</FieldLabel>
                      {/* Repeat enquiry: previous KTP photo, reused unless HR
                          uploads a replacement below. /uploads is auth-gated,
                          hence AuthImage rather than a plain <img>. */}
                      {ktpPhotoSourceKycId && ktpPhotoSourceUrl && (
                        <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3">
                          <AuthImage
                            src={resolveKtpPreviewSrc(ktpPhotoSourceUrl)}
                            alt="Previous KTP photo"
                            className="max-h-40 w-full rounded-md object-contain bg-gray-50"
                            fallback={
                              <div className="flex h-24 items-center justify-center rounded-md bg-gray-50 text-xs text-gray-400">
                                Previous KTP photo
                              </div>
                            }
                          />
                          <p className="text-xs text-gray-500">
                            Using the KTP photo from this employee&apos;s previous
                            enquiry. Upload a new photo below to replace it.
                          </p>
                        </div>
                      )}
                      <Controller
                        name="ktpPhoto"
                        control={control}
                        render={({ field }) => (
                          <MyDropzone
                            // MyDropzone only reads its files on mount, so remount
                            // on every pick / target switch (incl. re-picking the
                            // same employee) — else a stale file stays on screen
                            // while the form sends the reused photo instead.
                            key={`ktp-photo-${ktpDropzoneSeq}`}
                            accept={['.jpg', '.jpeg', '.png', '.webp']}
                            maxSize={5 * 1024 * 1024}
                            multiple={false}
                            showImage
                            onChange={(files) => {
                              field.onChange(files)
                              // A new upload replaces the previous KTP photo.
                              if (files?.length) {
                                setValue('ktpPhotoSourceKycId', null)
                                setValue('ktpPhotoSourceUrl', null)
                              }
                            }}
                          />
                        )}
                      />
                      {errors?.ktpPhoto?.message && (
                        <p className="text-sm text-error/600">
                          {errors.ktpPhoto.message}
                        </p>
                      )}
                    </div>

                    {/* NIK */}
                    <div className="flex flex-col gap-0.5">
                      <FieldLabel required>NIK</FieldLabel>
                      <MyTextField
                        name="ktpNumber"
                        control={control}
                        placeholder="16-digit NIK"
                        errors={errors?.ktpNumber?.message}
                        startAdornment={<CreditCard01 className="size-4 text-gray-400" />}
                      />
                    </div>

                    {/* Name on KTP */}
                    <div className="flex flex-col gap-0.5">
                      <FieldLabel required>Name on KTP</FieldLabel>
                      <MyTextField
                        name="ktpName"
                        control={control}
                        placeholder="As printed on the KTP"
                        errors={errors?.ktpName?.message}
                        startAdornment={<User01 className="size-4 text-gray-400" />}
                      />
                    </div>

                    {/* Date of birth */}
                    <Controller
                      name="ktpDob"
                      control={control}
                      render={({ field }) => (
                        <MyCalendar
                          value={field.value}
                          onChange={field.onChange}
                          maxDate={(() => {
                            const d = new Date()
                            d.setHours(0, 0, 0, 0)
                            return d
                          })()}
                          target={(open, show) => (
                            <div className="flex flex-col gap-1.5">
                              <FieldLabel required>Date of birth</FieldLabel>
                              <MyButton
                                type="button"
                                color="secondary"
                                variant="outlined"
                                size="md"
                                expanded
                                onClick={show}
                                customClassname={
                                  errors.ktpDob
                                    ? '!border-red-300 !ring-1 !ring-red-300'
                                    : 'hover:!ring-2 hover:!ring-brand/400'
                                }
                              >
                                <div className="flex w-full items-center gap-3 text-left">
                                  <Calendar className="size-4 text-gray-400 shrink-0" />
                                  <div className="flex flex-1 flex-col overflow-hidden">
                                    {field.value ? (
                                      <span className="text-sm font-medium text-gray-900">
                                        {format(field.value, 'MMM d, yyyy')}
                                      </span>
                                    ) : (
                                      <span className="text-md-regular text-gray-light/500">
                                        Select date
                                      </span>
                                    )}
                                  </div>
                                  {field.value ? (
                                    <XClose
                                      className="size-5 text-gray-400 hover:text-gray-600 shrink-0"
                                      strokeWidth={2}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        field.onChange(null)
                                      }}
                                    />
                                  ) : (
                                    <ChevronDown
                                      className="size-5 text-gray-400 shrink-0"
                                      strokeWidth={2}
                                    />
                                  )}
                                </div>
                              </MyButton>
                              {errors.ktpDob && (
                                <p className="text-sm text-error/600">
                                  {errors.ktpDob.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      )}
                    />

                    {/* Gender */}
                    <SelectField
                      label="Gender"
                      required
                      name="ktpGender"
                      control={control}
                      options={KTP_GENDER_OPTIONS}
                      placeholder="Select gender"
                      errors={errors?.ktpGender?.message ?? errors?.ktpGender?.value?.message}
                      startAdornment={<User01 className="size-4 text-gray-400" />}
                    />

                    {/* City (CLIK region — carries the bureau city code) */}
                    <div className="flex flex-col gap-1.5">
                      <FieldLabel required>City</FieldLabel>
                      <MyAsyncDropdown
                        trigger={trigger}
                        name="ktpCity"
                        control={control}
                        placeholder="Search city / regency"
                        error={errors?.ktpCity?.message}
                        value={ktpCity}
                        asyncFunction={searchClikCities}
                        isOptionEqualToValue={(option, value) => option?.code === value?.code}
                        getOptionLabel={(e) => e?.name || ''}
                        onChange={(_e, value) =>
                          setValue('ktpCity', value, { shouldDirty: true, shouldValidate: true })
                        }
                      />
                    </div>

                    {/* District */}
                    <div className="flex flex-col gap-0.5">
                      <FieldLabel required>District</FieldLabel>
                      <MyTextField
                        name="ktpDistrict"
                        control={control}
                        placeholder="District (kecamatan)"
                        errors={errors?.ktpDistrict?.message}
                        startAdornment={<MarkerPin01 className="size-4 text-gray-400" />}
                      />
                    </div>

                    {/* Subdistrict */}
                    <div className="flex flex-col gap-0.5">
                      <FieldLabel required>Subdistrict</FieldLabel>
                      <MyTextField
                        name="ktpSubdistrict"
                        control={control}
                        placeholder="Subdistrict (kelurahan / desa)"
                        errors={errors?.ktpSubdistrict?.message}
                        startAdornment={<MarkerPin01 className="size-4 text-gray-400" />}
                      />
                    </div>

                    {/* Postal code */}
                    <div className="flex flex-col gap-0.5">
                      <FieldLabel>Postal code</FieldLabel>
                      <MyTextField
                        name="ktpPostalCode"
                        control={control}
                        placeholder="5-digit postal code"
                        errors={errors?.ktpPostalCode?.message}
                        startAdornment={<MarkerPin01 className="size-4 text-gray-400" />}
                      />
                    </div>

                    {/* Address */}
                    <div className="flex flex-col gap-0.5">
                      <FieldLabel required>Address</FieldLabel>
                      <MyTextField
                        name="ktpAddress"
                        control={control}
                        placeholder="Address as printed on the KTP"
                        errors={errors?.ktpAddress?.message}
                        startAdornment={<MarkerPin01 className="size-4 text-gray-400" />}
                      />
                    </div>

                    {/* WhatsApp (optional) */}
                    <div className="flex flex-col gap-0.5">
                      <FieldLabel>WhatsApp (optional)</FieldLabel>
                      <MyTextField
                        name="ktpWhatsapp"
                        control={control}
                        placeholder="e.g. 0812xxxxxxx"
                        maxLength={15}
                        errors={errors?.ktpWhatsapp?.message}
                        startAdornment={<WhatsApp className="size-4 text-green-500" />}
                      />
                    </div>
                  </div>
                </MyDoubleCard>
              )}

              {/* HR supporting documents card (per-enquiry, holding mode 'hr').
                  Job application form: Candidate + job mode 'hr'. T&C: mode 'hr'
                  for either target. */}
              {(jobFormByHr || tncByHr) && (
                <MyDoubleCard heading="Supporting documents" innerClassName="p-4">
                  <div className="flex flex-col gap-5">
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Upload the HR-supplied files used in place of the candidate&apos;s
                      input. PDF, DOC or DOCX (max 10MB).
                    </p>
                    {jobFormByHr && (
                      <div className="flex flex-col gap-1.5">
                        <FieldLabel required>Job application form</FieldLabel>
                        <Controller
                          name="jobFormFile"
                          control={control}
                          render={({ field }) => (
                            <MyDropzone
                              accept={['.pdf', '.doc', '.docx']}
                              maxSize={10 * 1024 * 1024}
                              multiple={false}
                              onChange={(files) => field.onChange(files)}
                            />
                          )}
                        />
                        {errors?.jobFormFile?.message && (
                          <p className="text-sm text-error/600">{errors.jobFormFile.message}</p>
                        )}
                      </div>
                    )}
                    {tncByHr && (
                      <div className="flex flex-col gap-1.5">
                        <FieldLabel required>Consent for terms and condition</FieldLabel>
                        <Controller
                          name="tncFile"
                          control={control}
                          render={({ field }) => (
                            <MyDropzone
                              accept={['.pdf', '.doc', '.docx']}
                              maxSize={10 * 1024 * 1024}
                              multiple={false}
                              onChange={(files) => field.onChange(files)}
                            />
                          )}
                        />
                        {errors?.tncFile?.message && (
                          <p className="text-sm text-error/600">{errors.tncFile.message}</p>
                        )}
                      </div>
                    )}
                  </div>
                </MyDoubleCard>
              )}

              {/* Request option card (Employee only) */}
              {category === 'Employee' && (
              <MyDoubleCard heading="Request option" innerClassName="p-4">
                <div className="flex flex-col gap-5">
                  {/* Consent Expiry */}
                  <Controller
                    name="consentExpiry"
                    control={control}
                    render={({ field }) => (
                      <MyCalendar
                        value={field.value}
                        onChange={field.onChange}
                        minDate={(() => {
                          const d = new Date()
                          d.setHours(0, 0, 0, 0)
                          return d
                        })()}
                        target={(open, show) => (
                          <div className="flex flex-col gap-1.5">
                            <FieldLabel required>Consent Expiry</FieldLabel>
                            <MyButton
                              type="button"
                              color="secondary"
                              variant="outlined"
                              size="md"
                              expanded
                              onClick={show}
                              customClassname={
                                errors.consentExpiry
                                  ? '!border-red-300 !ring-1 !ring-red-300'
                                  : 'hover:!ring-2 hover:!ring-brand/400'
                              }
                            >
                              <div className="flex w-full items-center gap-3 text-left">
                                <Calendar className="size-4 text-gray-400 shrink-0" />
                                <div className="flex flex-1 flex-col overflow-hidden">
                                  {field.value ? (
                                    <span className="text-sm font-medium text-gray-900">
                                      {format(field.value, 'MMM d, yyyy')}
                                    </span>
                                  ) : (
                                    <span className="text-md-regular text-gray-light/500">
                                      Select date
                                    </span>
                                  )}
                                </div>
                                {field.value ? (
                                  <XClose
                                    className="size-5 text-gray-400 hover:text-gray-600 shrink-0"
                                    strokeWidth={2}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      field.onChange(null)
                                    }}
                                  />
                                ) : (
                                  <ChevronDown
                                    className="size-5 text-gray-400 shrink-0"
                                    strokeWidth={2}
                                  />
                                )}
                              </div>
                            </MyButton>
                            {errors.consentExpiry && (
                              <p className="text-sm text-error/600">
                                {errors.consentExpiry.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    )}
                  />

                  {/* Repeat Every */}
                  <div className="flex flex-col gap-0.5">
                    <FieldLabel required>Repeat Every</FieldLabel>
                    <Controller
                      name="repeatEvery"
                      control={control}
                      render={({ field }) => (
                        <MyAsyncDropdown
                          name="repeatEvery"
                          placeholder="Select interval"
                          value={field.value}
                          error={
                            errors?.repeatEvery?.message ??
                            errors?.repeatEvery?.value?.message
                          }
                          startAdornment={
                            <RefreshCcw05 className="size-4 text-gray-400" />
                          }
                          asyncFunction={fetchRepeatEveryOptions}
                          isOptionEqualToValue={(opt, val) =>
                            opt?.value === val?.value
                          }
                          getOptionLabel={(e) => e?.label || ''}
                          onChange={(_e, val) => field.onChange(val)}
                          renderOption={(option) => (
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {option.label}
                              </span>
                              {option.subLabel && (
                                <span className="text-xs text-gray-500">
                                  {option.subLabel}
                                </span>
                              )}
                            </div>
                          )}
                        />
                      )}
                    />
                  </div>
                </div>
              </MyDoubleCard>
              )}
            </div>
          </SimpleBar>
        </section>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <MyButton
            type="button"
            color="secondary"
            variant="outlined"
            size="md"
            onClick={handleClose}
          >
            Cancel
          </MyButton>

          <MyButton
            type="submit"
            color="primary"
            variant="filled"
            size="md"
            disabled={isStacked || isSubmitting}
          >
            Submit
          </MyButton>
        </footer>
      </form>

      <ConfirmEnquiryPaymentModal
        open={payOpen}
        companyId={pendingCreate ? pendingCreate.companyId : null}
        preview={payPreview}
        onClose={() => { setPayOpen(false); setPendingCreate(null) }}
        // Finalise the create with the chosen payment fields; returns the create
        // response so the modal can read gateway_payment (VA). On the saldo path
        // it closes the whole slider; on gateway it stays to show the VA.
        createFn={async (paymentFields) => {
          const { payload, attachments } = pendingCreate
          const res = await finalizeCreate({ ...payload, ...paymentFields }, attachments, {
            closeOnDone: paymentFields.payment_method !== 'gateway',
          })
          if (paymentFields.payment_method !== 'gateway') {
            setPayOpen(false); setPendingCreate(null)
          } else {
            // gateway: refresh the list behind the modal; the modal shows the VA.
            getList()
          }
          return res
        }}
        onFinishGateway={() => { setPayOpen(false); setPendingCreate(null); handleCurrentSlider(null) }}
      />
    </div>
  )
}

export default NewEnquirySlider
